import React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import ComponentHeaderTitles from '../ComponentHeaderTitles';
import { FormHelperText } from '@mui/material';
/**
 * Renders a component for inputting the user's energy usage.
 * If the user has already estimated their energy usage, the values will be pre-filled.
 * If the user manually inputs their energy usage, the values will be stored in the finalChosenUsage state, which
 * will be used to pre-fill the values if they navigate back to this step.
 * @param {Object} props - The component props.
 * @param {Array} props.chosenProduct - An array of chosen products (e.g. "Electriciteit", "Gas").
 * @param {Object} props.verbruikEstimaterStep2Output - The output of the VerbruikEstimaterStep2 component.
 * @param {Object} props.finalChosenUsage - The final chosen energy usage.
 * @param {Function} props.setFinalChosenUsage - A function to set the final chosen energy usage.
 * @returns {JSX.Element} - The rendered component.
 */
const VerbruikInput = ({
    chosenProduct,
    verbruikEstimaterStep2Output,
    finalChosenUsage,
    setFinalChosenUsage,
    onUpdateValidationErrors
}) => {
    const [estimatedVerbruik, setEstimatedVerbruik] = React.useState(() => {
        if (finalChosenUsage) {
            return finalChosenUsage;
        } else if (verbruikEstimaterStep2Output) {
            return verbruikEstimaterStep2Output;
        }
        return {}; // Default to an empty object if no initial value is provided
    });

    const [gasInputError, setGasInputError] = React.useState(false);
    const [eHighInputError, setEHighInputError] = React.useState(false);
    const [eLowInputError, setELowInputError] = React.useState(false);

    const handleOnBlur = (field, value) => {
        // check if input is empty, null or undefined
        if (value === '' || value === null || value === undefined) {
            // set the error state to true
            if (field === 'gas') {
                setGasInputError(true);
            } else if (field === 'e_high') {
                setEHighInputError(true);
            } else if (field === 'e_low') {
                setELowInputError(true);
            }
        } else {
            // set the error state to false
            if (field === 'gas') {
                setGasInputError(false);
            } else if (field === 'e_high') {
                setEHighInputError(false);
            } else if (field === 'e_low') {
                setELowInputError(false);
            }
        }
    }

    useEffect(() => {
        if (!gasInputError && !eHighInputError && !eLowInputError) {
            onUpdateValidationErrors([]);
        } else {
            gasInputError && onUpdateValidationErrors(['Gelieve je gasverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.', false]);
            eHighInputError && onUpdateValidationErrors(['Gelieve je hoog stroomverbruik in te vullen. Verbruik je geen hoog stroomverbruik', false]);
            eLowInputError && onUpdateValidationErrors(['Gelieve je laag stroomverbruik in te vullen. Verbruik je geen laag stroomverbruik', false]);

        };

    }, [gasInputError, eHighInputError, eLowInputError]);


    const handleChange = (field, value) => {
        const newValue = value.trim();
        const re = /^[0-9\b]+$/;
        // If value is empty or passes the regex test, update the state
        if (newValue === "" || re.test(newValue)) {
            setEstimatedVerbruik(prevValues => {
                // Create a new object with the updated field value
                const updatedValues = { ...prevValues, [field]: newValue };

                // Update the finalChosenUsage in the parent component
                // This function should correctly merge the new field value with the existing state
                setFinalChosenUsage(prevFinalChosenUsage => ({
                    ...prevFinalChosenUsage,
                    [field]: newValue
                }));

                return updatedValues;
            });
        }
    };


    /**
     * On mount, check if the finalChosenUsage is already set. If not, set it to the verbruikEstimaterStep2Output (if it exists)
     * This ensures that if the user accepts the estimated values, the estimated values will be used to set the finalChosenUsage.
     */
    React.useEffect(() => {
        // Only update the finalChosenUsage if it's not already set
        if (verbruikEstimaterStep2Output) {
            // filter out properties that are not e_high, e_low, e_single or gas
            const filteredOutput = Object.keys(verbruikEstimaterStep2Output)
                .filter(key => ['e_high', 'e_low', 'e_single', 'gas'].includes(key))
                .reduce((obj, key) => {
                    obj[key] = verbruikEstimaterStep2Output[key];
                    return obj;
                }, {});
            setEstimatedVerbruik(filteredOutput); // immediately update the local state
            setFinalChosenUsage(filteredOutput); // update the finalChosenUsage in the parent component
        } else if (!finalChosenUsage && !verbruikEstimaterStep2Output) {
            // create an empty key for each chosen product
            const emptyValues = {};
            chosenProduct.forEach((product) => {
                if (product === "Electriciteit") {
                    emptyValues.e_high = null;
                    emptyValues.e_low = null;
                } else if (product === "Gas") {
                    emptyValues.gas = null;
                }
            });
            setFinalChosenUsage(emptyValues);
        }
    }, []); // The empty dependency array ensures this effect only runs once on mount



    return (
        <div>
            <ComponentHeaderTitles
                title="Wat is je verbruik?"
            />
            {chosenProduct.map((product, index) => (
                <div key={index}>
                    {product === "Electriciteit" ? (
                        <>
                            <div class="verbruik-stroom-hoog">
                                <FormControl variant="outlined">
                                    <Typography variant="caption" color="textSecondary">
                                        Stroom hoog
                                    </Typography>
                                    <OutlinedInput
                                        type="text"
                                        id="outlined-adornment-weight"
                                        placeholder="0000"
                                        endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <span className="icon-as-stroomlicht icon-size" />
                                            </InputAdornment>
                                        }

                                        aria-describedby="outlined-weight-helper-text"
                                        className='verbruik-input'
                                        inputProps={{
                                            'aria-label': 'weight',
                                            inputMode: "numeric"
                                        }}
                                        value={estimatedVerbruik && estimatedVerbruik.e_high ? estimatedVerbruik.e_high : ''}
                                        onChange={(e) => handleChange('e_high', e.target.value)}
                                        onBlur={(e) => handleOnBlur('e_high', e.target.value)}
                                        error={eHighInputError}
                                        helperText={eHighInputError && 'Gelieve je hoog stroomverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                    />

                                    {eHighInputError && (
                                        <FormHelperText error={eHighInputError}>
                                            Gelieve je hoog stroomverbruik in te vullen. Verbruik je geen hoog stroom? Vul dan 0 in.
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                            <div class="verbruik-stroom-laag">
                                <FormControl variant="outlined">
                                    <Typography variant="caption" color="textSecondary">
                                        Stroom laag
                                    </Typography>
                                    <OutlinedInput
                                        type="text"
                                        id="outlined-adornment-weight"
                                        placeholder="0000"
                                        endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <span className="icon-as-stroomlicht icon-size" />
                                            </InputAdornment>
                                        }
                                        aria-describedby="outlined-weight-helper-text"
                                        className='verbruik-input'
                                        inputProps={{
                                            'aria-label': 'weight',
                                            inputMode: "numeric"
                                        }}
                                        value={estimatedVerbruik && estimatedVerbruik.e_low ? estimatedVerbruik.e_low : ''}
                                        onChange={(e) => handleChange('e_low', e.target.value)}
                                        onBlur={(e) => handleOnBlur('e_low', e.target.value)}
                                        error={eLowInputError}
                                        helperText={eLowInputError && 'Gelieve je laag stroomverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                    />
                                    {eLowInputError && (
                                        <FormHelperText error={eLowInputError}>
                                            Gelieve je laag stroomverbruik in te vullen. Verbruik je geen laag stroom? Vul dan 0 in.
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                        </>
                    ) : (
                        <>
                            <div class="verbruik-gas">
                                <FormControl variant="outlined">
                                    <Typography variant="caption" color="textSecondary">
                                        Gas
                                    </Typography>
                                    <OutlinedInput
                                        type="text"
                                        id="outlined-adornment-weight"
                                        endAdornment={<InputAdornment position="end">M³</InputAdornment>}
                                        placeholder="0000"
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <span className="icon-as-gaslicht icon-size" />
                                            </InputAdornment>
                                        }
                                        aria-describedby="outlined-weight-helper-text"
                                        className='verbruik-input'
                                        inputProps={{
                                            'aria-label': 'weight',
                                            inputMode: "numeric"
                                        }}
                                        value={estimatedVerbruik && estimatedVerbruik.gas ? estimatedVerbruik.gas : ''}
                                        onChange={(e) => handleChange('gas', e.target.value)}
                                        onBlur={(e) => handleOnBlur('gas', e.target.value)}
                                        error={gasInputError}
                                        helperText={gasInputError && 'Gelieve je gasverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                    />
                                    {gasInputError && (
                                        <FormHelperText error={gasInputError}>
                                            Gelieve je gasverbruik in te vullen. Verbruik je geen gas? Vul dan 0 in.
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}
VerbruikInput.displayName = 'VerbruikInput';
export default VerbruikInput;
